import React, { useState } from "react";
import { CheckCircle, XCircle, AlertCircle, X } from "lucide-react";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    projectDetails: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [notification, setNotification] = useState(null);

  const Notification = ({ type, title, message, onClose }) => {
    const icons = {
      success: CheckCircle,
      error: XCircle,
      warning: AlertCircle,
    };

    const colors = {
      success: "bg-gradient-to-br from-green-50 to-emerald-50 border-green-200",
      error: "bg-gradient-to-br from-red-50 to-rose-50 border-red-200",
      warning: "bg-gradient-to-br from-yellow-50 to-amber-50 border-yellow-200",
    };

    const iconColors = {
      success: "text-green-500",
      error: "text-red-500",
      warning: "text-amber-500",
    };

    const buttonColors = {
      success: "bg-green-500 hover:bg-green-600",
      error: "bg-red-500 hover:bg-red-600",
      warning: "bg-amber-500 hover:bg-amber-600",
    };

    const Icon = icons[type];

    return (
      <>
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 animate-fade-in">
          <div className="fixed inset-0 flex items-start justify-center p-4 mt-20">
            <div
              className={`${colors[type]} border-2 rounded-2xl shadow-2xl max-w-md w-full transform transition-all duration-500 animate-modal-in backdrop-blur-sm`}
            >
              <div className="text-center pt-8 pb-4">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white shadow-lg mb-4">
                  <Icon className={`${iconColors[type]} w-8 h-8`} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">
                  {title}
                </h3>
              </div>
              <div className="px-8 pb-6">
                <p className="text-gray-600 text-center leading-relaxed">
                  {message}
                </p>
              </div>
              <div className="px-8 pb-8 flex gap-3">
                <button
                  onClick={onClose}
                  className={`${buttonColors[type]} text-white px-6 py-3 rounded-xl font-semibold flex-1 transition-all duration-200 hover:scale-105 shadow-lg`}
                >
                  {type === "success"
                    ? "Great!"
                    : type === "error"
                      ? "Try Again"
                      : "Got it"}
                </button>
                <button
                  onClick={onClose}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-4 py-3 rounded-xl font-medium transition-all duration-200 hover:scale-105"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  const showNotification = (type, title, message, duration = 5000) => {
    setNotification({ type, title, message });
    setTimeout(() => {
      setNotification(null);
    }, duration);
  };

  // Indian mobile numbers: 10 digits, first digit 6-9. formData.phone stores
  // ONLY the raw 10-digit number (no country code, no spaces) so typing and
  // backspacing behave normally. The +91 is shown as a fixed prefix in the UI
  // and re-attached only when submitting.
  const validatePhoneNumber = (phone) => {
    return /^[6-9]\d{9}$/.test(phone);
  };

  // Purely visual grouping for display: "98765 43210"
  const formatPhoneDisplay = (digits) => {
    if (digits.length > 5) {
      return `${digits.slice(0, 5)} ${digits.slice(5)}`;
    }
    return digits;
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    if (name === "phone") {
      // Strip everything except digits, then drop a leading "91" the user
      // may have typed/pasted (e.g. from "+91 98765..."), and cap at 10.
      let digits = value.replace(/\D/g, "");
      if (digits.length > 10 && digits.startsWith("91")) {
        digits = digits.slice(2);
      }
      digits = digits.slice(0, 10);

      setFormData({ ...formData, phone: digits });
      if (formErrors.phone && validatePhoneNumber(digits)) {
        setFormErrors({ ...formErrors, phone: "" });
      }
    } else {
      setFormData({ ...formData, [name]: value });
      if (formErrors[name] && value.trim()) {
        setFormErrors({ ...formErrors, [name]: "" });
      }
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Name is required";
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }
    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (!validatePhoneNumber(formData.phone)) {
      errors.phone = "Please enter a valid 10-digit mobile number";
    }
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      showNotification(
        "warning",
        "Form Incomplete",
        "Please fill in all required fields correctly.",
      );
      return;
    }

    try {
      showNotification(
        "warning",
        "Processing...",
        "Submitting your inquiry, please wait.",
        3000,
      );
      // Store phone as 91XXXXXXXXXX (country code + 10-digit number)
      const submissionData = { ...formData, phone: "91" + formData.phone };

      const response = await fetch(
        "https://archpoint.onrender.com/api/inquiry",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(submissionData),
        },
      );

      if (!response.ok) throw new Error("Failed to send inquiry.");

      showNotification(
        "success",
        "Inquiry Submitted!",
        "Thank you for your inquiry! We will contact you soon.",
      );
      setFormData({ name: "", email: "", phone: "", projectDetails: "" });
      setFormErrors({});
    } catch (error) {
      console.error("Error:", error);
      showNotification(
        "error",
        "Submission Failed",
        "Something went wrong. Please try again later.",
      );
    }
  };

  return (
    <>
      <section id="contact" className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Ready to Start Your Dream Project?
            </h2>
            <p className="text-xl text-gray-600">
              Let's bring your vision to reality with our expert consultation
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-300 ${
                    formErrors.name
                      ? "border-red-500 bg-red-50"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter your name"
                />
                {formErrors.name && (
                  <p className="text-red-500 text-sm mt-1 animate-shake">
                    {formErrors.name}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleFormChange}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-300 ${
                    formErrors.email
                      ? "border-red-500 bg-red-50"
                      : "border-gray-300"
                  }`}
                  placeholder="Enter your email"
                />
                {formErrors.email && (
                  <p className="text-red-500 text-sm mt-1 animate-shake">
                    {formErrors.email}
                  </p>
                )}
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone *
              </label>
              <div
                className={`flex items-stretch w-full border rounded-lg overflow-hidden transition-all duration-300 focus-within:ring-2 focus-within:ring-yellow-500 ${
                  formErrors.phone
                    ? "border-red-500 bg-red-50"
                    : "border-gray-300"
                }`}
              >
                <span className="flex items-center px-4 bg-gray-50 text-gray-600 font-medium border-r border-gray-300 select-none">
                  +91
                </span>
                <input
                  type="tel"
                  inputMode="numeric"
                  name="phone"
                  value={formatPhoneDisplay(formData.phone)}
                  onChange={handleFormChange}
                  className="flex-1 min-w-0 px-4 py-3 bg-transparent focus:outline-none"
                  placeholder="98765 43210"
                />
              </div>
              {formErrors.phone && (
                <p className="text-red-500 text-sm mt-1 animate-shake">
                  {formErrors.phone}
                </p>
              )}
              {!formErrors.phone &&
                formData.phone &&
                validatePhoneNumber(formData.phone) && (
                  <p className="text-green-600 text-sm mt-1">
                    ✓ Valid phone number format
                  </p>
                )}
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Details
              </label>
              <textarea
                name="projectDetails"
                value={formData.projectDetails}
                onChange={handleFormChange}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all duration-300"
                placeholder="Tell us about your dream project..."
              />
            </div>
            <button
              onClick={handleSubmit}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-4 rounded-lg text-base sm:text-lg transition-all duration-300 active:transform active:scale-95 shadow-lg hover:shadow-xl"
            >
              <span className="sm:hidden">Submit Inquiry</span>
              <span className="hidden sm:inline">
                Submit Inquiry - Let's Make Your Dream Reality
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* Notification Popup */}
      {notification && (
        <Notification
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <style>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes modal-in {
          from { opacity: 0; transform: scale(0.8) translateY(-20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-fade-in { animation: fade-in 0.3s ease-out; }
        .animate-modal-in { animation: modal-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); }
        .animate-shake { animation: shake 0.5s ease-in-out; }
      `}</style>
    </>
  );
};

export default ContactSection;  